import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useQuery } from "@apollo/client";
import useSchools from "MyCalendar/hooks/useSchools";
import { GradesContext } from "./context/GradesContext";
import { useGradeHierarchy, useVisibleGrades, useGradeDetails } from "./hooks";
import useSchoolSelection from "AppComponents/SharedEntities/SelectParticipantsModal/hooks/useSchoolSelection";
import {
  filterGradesBySearch,
  getFilteredSchoolGradeIds,
} from "./modules/GradesUtils";
import { getUserCountByEntityandEntityIdsQuery } from "AppComponents/SharedEntities/Courses/modules/CoursesQueries";

const DEFAULT_USER_TYPES = ["STAFF", "STUDENT", "FAMILY_MEMBER"];
const EMPTY_FILTERS = {};

const GradesProvider = ({
  children,
  organizationId,
  academicYearId,
  selectedUserTypes,
  gradeFilters,
  viewOnly,
  isActive,
  onSelectionChange,
  // External state overrides (lifted from SelectParticipantsContext when inside modal)
  selectedGradeList: externalSelectedGradeList,
  setSelectedGradeList: externalSetSelectedGradeList,
  searchText: externalSearchText,
  setSearchText: externalSetSearchText,
  selectedSchoolIds: externalSelectedSchoolIds,
  setSelectedSchoolIds: externalSetSelectedSchoolIds,
}) => {
  // 1. Schools from Redux
  const { schools } = useSchools();

  // 2. School selection — use external state if provided, otherwise manage internally
  const [internalSchoolIds, setInternalSchoolIds] = useState(null);
  const selectedSchoolIds =
    externalSelectedSchoolIds !== undefined
      ? externalSelectedSchoolIds
      : internalSchoolIds;
  const setSelectedSchoolIds =
    externalSetSelectedSchoolIds || setInternalSchoolIds;

  // Initialise for single-school orgs when schools load from Redux
  useEffect(() => {
    if (externalSelectedSchoolIds !== undefined) return;
    if (_.size(schools) === 1) {
      setSelectedSchoolIds(new Set([_.get(schools, "0.id")]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schools]);

  const {
    onSchoolChange,
    isAllSchoolsSelected,
    selectedSchoolLabel,
    allSchoolIds,
  } = useSchoolSelection(schools, selectedSchoolIds, setSelectedSchoolIds);

  // 3. Selection + search state — use external overrides if provided
  // (resolved early so hooks below can use searchText)
  const [internalSelectedGradeList, setInternalSelectedGradeList] = useState(
    []
  );
  const [internalSearchText, setInternalSearchText] = useState("");

  const selectedGradeList =
    externalSelectedGradeList !== undefined
      ? externalSelectedGradeList
      : internalSelectedGradeList;
  const resolvedSetSelectedGradeList =
    externalSetSelectedGradeList || setInternalSelectedGradeList;
  const searchText =
    externalSearchText !== undefined ? externalSearchText : internalSearchText;
  const resolvedSetSearchText = externalSetSearchText || setInternalSearchText;

  // 4. Phase 1: Fetch all grade IDs, build hierarchy
  const {
    hierarchy,
    allGradeIds,
    loading: hierarchyLoading,
  } = useGradeHierarchy(
    organizationId,
    gradeFilters,
    !isActive,
    academicYearId
  );

  // 5. IntersectionObserver for viewport tracking
  const { visibleGradeIds, registerGradeRef } = useVisibleGrades(viewOnly);

  // 6. Phase 2: Fetch details + counts for visible grades
  // When searching, fetch all grade IDs so search can match against all names
  const { gradeDetailsMap, loading: detailsLoading } = useGradeDetails(
    visibleGradeIds,
    viewOnly,
    undefined
  );

  const effectiveSchoolIdsForCountQuery = selectedSchoolIds
    ? [...selectedSchoolIds]
    : allSchoolIds;

  // 7. All-schools aggregate counts (for SelectAllRow subtitle)
  const {
    data: allSchoolsCountData,
    loading: allSchoolsCountLoading,
  } = useQuery(getUserCountByEntityandEntityIdsQuery, {
    variables: {
      organizationId,
      input: {
        entity: "CURRICULUM_PROGRAM",
        entityIds: effectiveSchoolIdsForCountQuery,
      },
    },
    context: { useHttpLink: true },
    skip:
      _.isEmpty(effectiveSchoolIdsForCountQuery) ||
      !organizationId ||
      viewOnly ||
      !isActive,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const allSchoolsCount = useMemo(
    () => _.get(allSchoolsCountData, "node.users.userCountByEntity", {}),
    [allSchoolsCountData]
  );

  // Notify parent on selection change
  useEffect(() => {
    onSelectionChange(selectedGradeList);
  }, [selectedGradeList, onSelectionChange]);

  // Grade IDs scoped to the currently selected schools
  const filteredSchoolGradeIds = useMemo(
    () => getFilteredSchoolGradeIds(hierarchy, selectedSchoolIds, allGradeIds),
    [hierarchy, selectedSchoolIds, allGradeIds]
  );

  // Filtered grade IDs based on search, scoped to selected schools
  const filteredGradeIds = useMemo(
    () =>
      filterGradesBySearch(filteredSchoolGradeIds, gradeDetailsMap, searchText),
    [filteredSchoolGradeIds, gradeDetailsMap, searchText]
  );

  // Stable setters
  const handleSetSelectedGradeList = useCallback(
    updater => resolvedSetSelectedGradeList(updater),
    [resolvedSetSelectedGradeList]
  );

  const handleSetSearchText = useCallback(text => resolvedSetSearchText(text), [
    resolvedSetSearchText,
  ]);

  // 8. Memoized context value
  const contextValue = useMemo(
    () => ({
      organizationId,
      selectedUserTypes,
      viewOnly,
      hierarchy,
      allSchoolIds,
      allGradeIds,
      hierarchyLoading,
      selectedSchoolIds,
      isAllSchoolsSelected,
      onSchoolChange,
      selectedSchoolLabel,
      schools,
      visibleGradeIds,
      registerGradeRef,
      gradeDetailsMap,
      detailsLoading,
      allSchoolsCount,
      allSchoolsCountLoading:
        allSchoolsCountLoading && _.isEmpty(allSchoolsCountData),
      selectedGradeList,
      setSelectedGradeList: handleSetSelectedGradeList,
      searchText,
      setSearchText: handleSetSearchText,
      filteredGradeIds,
      filteredSchoolGradeIds,
    }),
    [
      organizationId,
      selectedUserTypes,
      viewOnly,
      hierarchy,
      allSchoolIds,
      allGradeIds,
      hierarchyLoading,
      selectedSchoolIds,
      isAllSchoolsSelected,
      onSchoolChange,
      selectedSchoolLabel,
      schools,
      visibleGradeIds,
      registerGradeRef,
      gradeDetailsMap,
      detailsLoading,
      allSchoolsCount,
      allSchoolsCountLoading,
      allSchoolsCountData,
      selectedGradeList,
      handleSetSelectedGradeList,
      searchText,
      handleSetSearchText,
      filteredGradeIds,
      filteredSchoolGradeIds,
    ]
  );

  return (
    <GradesContext.Provider value={contextValue}>
      {children}
    </GradesContext.Provider>
  );
};

GradesProvider.propTypes = {
  children: PropTypes.node.isRequired,
  organizationId: PropTypes.string.isRequired,
  academicYearId: PropTypes.string,
  selectedUserTypes: PropTypes.arrayOf(PropTypes.string),
  gradeFilters: PropTypes.object,
  viewOnly: PropTypes.bool,
  isActive: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  selectedGradeList: PropTypes.array,
  setSelectedGradeList: PropTypes.func,
  searchText: PropTypes.string,
  setSearchText: PropTypes.func,
  selectedSchoolIds: PropTypes.instanceOf(Set),
  setSelectedSchoolIds: PropTypes.func,
};

GradesProvider.defaultProps = {
  academicYearId: undefined,
  selectedUserTypes: DEFAULT_USER_TYPES,
  gradeFilters: EMPTY_FILTERS,
  viewOnly: false,
  isActive: true,
  onSelectionChange: () => {},
  selectedGradeList: undefined,
  setSelectedGradeList: undefined,
  searchText: undefined,
  setSearchText: undefined,
  selectedSchoolIds: undefined,
  setSelectedSchoolIds: undefined,
};

export default GradesProvider;
